/**
 * Boerenbridge's view of the table.
 *
 * All the drawing lives in `TrickTable`; this is only the mapping from one
 * game's state onto it — the bid and tricks under each name, the trump card in
 * the corner. Hartenjagen has its own mapping and neither knows about the other.
 */

import type { LiveGameState } from '@shared/types/live';
import { SUIT_SYMBOLS, bidBalance, describeBidBalance } from '@shared/game-logic';
import TrickTable, { type TableView } from '@/components/TrickTable';
import { PlayingCard } from '@/components/PlayingCard';
import { trumpLabel } from '@/lib/labels';

export default function BoerenbridgeTable({
  state,
  mySeat,
  blind,
}: {
  state: LiveGameState;
  mySeat: number | null;
  blind: boolean;
}) {
  /*
   * How far the bids are off the tricks. Shown from the moment the last player
   * has bid — before that the running total says more about who is still to
   * speak than about the round.
   */
  const balance = bidBalance(
    state.cards,
    state.players.map((player) => player.bid)
  );

  const view: TableView = {
    players: state.players.map((player) => ({
      seat: player.seat,
      name: player.name,
      avatar: player.avatar,
      emoji: player.emoji,
      isConnected: player.isConnected,
      handCount: player.handCount,
      faceUpCard: player.hand?.[0] ?? null,
      detail:
        player.bid === null ? undefined : (
          <span key={player.bid} className="animate-pop-in">
            {player.tricksWon}/{player.bid}
          </span>
        ),
      badge:
        state.dealerSeat === player.seat ? (
          <span className="rounded bg-base-600 px-1 text-[0.65rem] text-slate-300">D</span>
        ) : undefined,
      spoken: `${player.name}, ${
        player.bid === null ? 'nog niet geboden' : `bood ${player.bid} en heeft ${player.tricksWon}`
      }`,
    })),
    currentTrick: state.currentTrick,
    lastTrick: state.lastTrick,
    turnSeat: state.turnSeat,
    scoring: state.phase === 'scoring',
    emptyLabel: state.phase === 'bidding' ? 'bieden…' : 'wie komt uit?',
    note:
      balance.complete && state.phase !== 'lobby' ? (
        <span className={balance.difference === 0 ? '' : 'font-medium'}>
          {describeBidBalance(balance)}
        </span>
      ) : undefined,
    spokenHeader: `Troef: ${trumpLabel(state.trumpSuit)}.${
      balance.complete ? ` ${describeBidBalance(balance)}.` : ''
    }`,
    corner: state.trumpCard ? (
      <>
        <PlayingCard card={state.trumpCard} size="sm" className="rotate-[-8deg]" />
        <span className="rounded-full bg-base-900/80 px-2 py-0.5 text-xs text-slate-300 backdrop-blur">
          {SUIT_SYMBOLS[state.trumpCard.suit]} troef
        </span>
      </>
    ) : (
      <span className="rounded-full bg-base-900/80 px-2.5 py-1 text-xs text-slate-300 backdrop-blur">
        troefloos
      </span>
    ),
  };

  return <TrickTable view={view} mySeat={mySeat} blind={blind} />;
}
